import React, {
  forwardRef, memo, useCallback, useMemo,
} from 'react';
import { func } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import _isEmpty from 'lodash/isEmpty';
import _uniqBy from 'lodash/uniqBy';
import Logger from '../../services/Logger';
import { panelsVisibilityShape, getIndicator, getCustomTextFieldPlaceholder } from './helpers';
import {
  isUploadStarted,
  requiredFields as requiredCustomFieldsSelector,
  selectedFieldsSelector,
} from '../../store/import/selectors';
import DetailsBlock from '../DetailsBlock';
import CustomFieldsSelector from '../CustomFieldsSelector';
import CustomField from '../Search/CustomField';
import { changeField, addCustomField, removeCustomField } from '../../store/import/actions';

const CustomFields = forwardRef(({ panelsVisibility, toggleVisibility }, ref) => {
  const dispatch = useDispatch();
  const requiredFields = useSelector(requiredCustomFieldsSelector);
  const allCustomFields = useSelector((state) => state.customFields.items);
  const allCustomGroups = useSelector((state) => state.customFields.groups);
  const editedCustomFields = useSelector((state) => state.import.fields.customFields || {});
  const addedCustomFields = useSelector((state) => state.import.addedCustomFields);
  const numberOfSelectedFiles = useSelector((state) => state.import.selected.length);
  const isCustomFieldsAllowed = useSelector((state) => Boolean(state.user?.subscriptionFeatures?.customFields));
  const hasPermission = useSelector((state) => state.import.permissions.editAssetMetadata);
  const disabled = useSelector(isUploadStarted);
  const errors = useSelector((state) => state.import.errors?.customFields || {});
  const selectedFields = useSelector(selectedFieldsSelector);
  const existedCustomFields = useMemo(
    () => selectedFields
      .reduce((result, { customFields = {} }) => (_isEmpty(customFields) ? result : [
        ...result,
        ...Object.entries(customFields).map(([title, data]) => ({ title, ...data })),
      ]), []),
    [selectedFields],
  );

  const requiredCustomFields = useMemo(
    () => (requiredFields.customFields || [])
      .map((title) => {
        const field = allCustomFields.find((cf) => cf.title === title);
        if (!field) return null;
        return {
          ...field,
          value: editedCustomFields[field.title]?.value || null,
          required: true,
        };
      })
      .filter(Boolean),
    [requiredFields.customFields, allCustomFields, editedCustomFields],
  );

  const selectedCustomFields = useMemo(
    () => _uniqBy(addedCustomFields.concat(existedCustomFields), 'title')
      /** Don't include required fields */
      .filter((cf) => {
        const index = requiredCustomFields.findIndex(({ title }) => title === cf.title);
        return index < 0;
      })
      .map((cf) => {
        let { value } = cf;
        const index = Object.keys(editedCustomFields).findIndex((title) => title === cf.title);
        if (index > -1) value = editedCustomFields[cf.title]?.value || null;
        return { ...cf, value };
      }),
    [addedCustomFields, editedCustomFields, existedCustomFields, requiredCustomFields],
  );

  const handleChange = useCallback((order, newValue, config) => {
    let value = newValue;
    const field = allCustomFields.find((cf) => cf.order === order);
    if (!field) return;

    if (config?.multipleAttach) {
      const oldValue = editedCustomFields[field.title]?.value || '';
      if (config?.isAttach) {
        value = `${oldValue},${newValue}`;
      } else {
        value = oldValue.split(',').filter((v) => v !== newValue).join(',');
      }
    }

    Logger.log('User', 'UploadPanelCustomFieldChanged', {
      title: field.title,
      assets_number: numberOfSelectedFiles,
    });

    dispatch(changeField({
      title: `customFields.${field.title}`,
      value: {
        ...field,
        value,
      },
    }));
  }, [dispatch, allCustomFields, editedCustomFields, numberOfSelectedFiles]);

  const handleAddCustomField = useCallback((cf) => {
    dispatch(addCustomField(cf));
  }, [dispatch]);

  const handleRemoveCustomField = useCallback(({ title }) => {
    dispatch(removeCustomField(title));
  }, [dispatch]);

  const getFieldGroups = useCallback((fields) => {
    const groupsHash = fields.reduce((acc, field) => {
      let separator = (allCustomGroups || []).find((group) => group.find(({ title }) => title === field.title))?.[0];
      if (!separator) {
        separator = {
          title: 'Custom fields',
          type: 'separator',
          visibility: 'visible',
        };
      }
      const { title } = separator;
      return acc[title] ? { ...acc, [title]: [...acc[title], field] } : { ...acc, [title]: [separator, field] };
    }, {});
    return Object.values(groupsHash);
  }, [allCustomGroups]);

  const hasErrors = useMemo(() => Boolean(Object.keys(errors || {}).length), [errors]);

  const indicator = useMemo(() => getIndicator('customFieldsRequired', requiredFields), [requiredFields]);

  const customFields = useMemo(() => requiredCustomFields.concat(selectedCustomFields), [requiredCustomFields, selectedCustomFields]);

  return (
    <>
      <If condition={customFields.length}>
        {getFieldGroups(customFields).map((group) => {
          const { title: blockTitle } = group[0];
          return (
            <DetailsBlock
              dataQa="customFields"
              ref={ref}
              detailsPanelVisibility={panelsVisibility.hasOwnProperty(blockTitle) ? panelsVisibility : { ...panelsVisibility, [blockTitle]: true }}
              toggleVisibility={toggleVisibility}
              blockName={blockTitle}
              blockTitle={blockTitle}
              indicator={indicator}
              error={hasErrors} // && l18n.IMPORT.textFieldIsRequired}
              errorHighlight
              openByDefault={hasErrors || indicator !== null}
            >
              <div key="required" className="itemsCustomFieldedAs">
                {
                  group.slice(1).map((field) => {
                    const { title, value, required } = field;
                    const error = errors[title]; // || l18n.IMPORT.textFieldIsRequired;

                    return (
                      <CustomField
                        key={title}
                        {...field}
                        placeholder={getCustomTextFieldPlaceholder(value)}
                        onChange={handleChange}
                        onRemove={handleRemoveCustomField}
                        disabled={disabled}
                        error={error}
                        required={required}
                        highlightChanged={false}
                      />
                    );
                  })
                }
              </div>
            </DetailsBlock>
          );
        })}
      </If>

      <If condition={!window.inbox}>
        {/* Custom fields add */}
        <CustomFieldsSelector
          className="importCustomFields"
          selectedFields={selectedCustomFields}
          addField={handleAddCustomField}
          removeField={handleRemoveCustomField}
          readOnly={!hasPermission}
          disabled={disabled || !isCustomFieldsAllowed}
          hideDisabled
          data-testid="importAddCustomField"
          disablePortal={window.innerWidth > 1024}
          hiddenFields={requiredFields.customFields || []}
        />
      </If>
    </>
  );
});

CustomFields.propTypes = {
  panelsVisibility: panelsVisibilityShape.isRequired,
  toggleVisibility: func.isRequired,
};

export default memo(CustomFields);
